import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Card, DateInput, TextButton, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';
import { useToastStore } from '../stores/toast';

interface UsageConfig {
  id: string;
  photoLimit: number;
  inspectionLimit: number;
  isEnabled: boolean;
  updatedAt: string;
}

interface UserUsage {
  id: string;
  userId: string;
  userName: string;
  userIdentifier: string | null;
  photoCount: number;
  inspectionCount: number;
  lastResetAt: string | null;
  resetBy: string | null;
  createdAt: string;
}

// 인라인 편집 컴포넌트
function InlineEditableNumber({
  value,
  max,
  onSave,
  colorClass,
}: {
  value: number;
  max: number;
  onSave: (newValue: number) => Promise<void>;
  colorClass: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // 외부에서 value가 변경되면 editValue도 업데이트
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value.toString());
    }
  }, [value, isEditing]);

  const handleSave = useCallback(async () => {
    const newValue = parseInt(editValue, 10);
    if (isNaN(newValue) || newValue < 0) {
      setEditValue(value.toString());
      setIsEditing(false);
      return;
    }

    if (newValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(newValue);
      setIsEditing(false);
    } catch {
      setEditValue(value.toString());
    } finally {
      setIsSaving(false);
    }
  }, [editValue, value, onSave]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value.toString());
      setIsEditing(false);
    }
  };

  const percent = max === 0 ? 100 : Math.min((value / max) * 100, 100);

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-24 bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${colorClass}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <input
          ref={inputRef}
          type="number"
          min="0"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          className="w-14 px-1 py-0.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <span className="text-sm text-gray-400">/{max}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="w-24 bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${colorClass}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <button
        onClick={() => setIsEditing(true)}
        className="text-sm text-gray-600 hover:text-brand-600 hover:underline cursor-pointer"
        title="클릭하여 수정"
      >
        {value}/{max}
      </button>
    </div>
  );
}

export default function UsageManagement() {
  const [confirm, confirmDialog] = useConfirmDialog();
  const [config, setConfig] = useState<UsageConfig | null>(null);
  const [usages, setUsages] = useState<UserUsage[]>([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    // KST 기준 오늘 날짜
    const kstOffset = 9 * 60 * 60 * 1000;
    const kstNow = new Date(now.getTime() + kstOffset);
    return kstNow.toISOString().split('T')[0];
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingConfig, setEditingConfig] = useState<{
    photoLimit: number;
    inspectionLimit: number;
  } | null>(null);
  const [total, setTotal] = useState(0);
  const showToast = useToastStore((state) => state.showToast);

  // 설정 및 사용량 목록 로드
  const loadData = async () => {
    setIsLoading(true);
    try {
      const [configRes, usageRes] = await Promise.all([
        adminApi.getUsageConfig(),
        adminApi.getUsersUsage({ date: selectedDate }),
      ]);

      if (configRes.success && configRes.data) {
        setConfig(configRes.data);
      }
      if (usageRes.success && usageRes.data) {
        setUsages(usageRes.data.usages);
        setTotal(usageRes.data.total);
      }
    } catch (error) {
      const err = extractApiError(error);
      showToast(err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  // 설정 저장
  const handleSaveConfig = async () => {
    if (!editingConfig) return;

    setIsSaving(true);
    try {
      const res = await adminApi.updateUsageConfig(editingConfig);
      if (res.success && res.data) {
        setConfig(res.data);
        setEditingConfig(null);
        showToast('설정이 저장되었습니다.', 'success');
      }
    } catch (error) {
      const err = extractApiError(error);
      showToast(err.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // 사용량 제한 활성화/비활성화 토글
  const handleToggleEnabled = async () => {
    if (!config) return;

    setIsSaving(true);
    try {
      const res = await adminApi.updateUsageConfig({ isEnabled: !config.isEnabled });
      if (res.success && res.data) {
        setConfig(res.data);
        showToast(
          res.data.isEnabled ? '사용량 제한이 활성화되었습니다.' : '사용량 제한이 비활성화되었습니다.',
          'success'
        );
      }
    } catch (error) {
      const err = extractApiError(error);
      showToast(err.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // 사용자 사용량 초기화
  const handleResetUsage = async (userId: string, userName: string) => {
    if (
      !(await confirm({
        title: '사용량 초기화',
        body: `${userName}님의 오늘 사용량을 초기화하시겠습니까?`,
        confirmLabel: '초기화',
        danger: true,
      }))
    )
      return;

    try {
      const res = await adminApi.resetUserUsage(userId);
      if (res.success) {
        showToast('사용량이 초기화되었습니다.', 'success');
        loadData();
      }
    } catch (error) {
      const err = extractApiError(error);
      showToast(err.message, 'error');
    }
  };

  // 사용자 사용량 수정
  const handleUpdateUsage = async (
    userId: string,
    field: 'photoCount' | 'inspectionCount',
    newValue: number,
  ) => {
    try {
      const res = await adminApi.updateUserUsage(userId, { [field]: newValue });
      if (res.success && res.data) {
        // 로컬 상태 업데이트
        setUsages((prev) =>
          prev.map((u) =>
            u.userId === userId
              ? { ...u, [field]: res.data![field] }
              : u
          )
        );
        showToast('사용량이 수정되었습니다.', 'success');
      }
    } catch (error) {
      const err = extractApiError(error);
      showToast(err.message, 'error');
      throw error; // 인라인 편집 컴포넌트에서 롤백하도록
    }
  };

  // 사용량 퍼센트 계산
  const getUsagePercent = (used: number, limit: number) => {
    if (limit === 0) return 100;
    return Math.min((used / limit) * 100, 100);
  };

  // 사용량 색상
  const getUsageColor = (used: number, limit: number) => {
    const percent = getUsagePercent(used, limit);
    if (percent >= 100) return 'bg-red-500';
    if (percent >= 80) return 'bg-yellow-500';
    return 'bg-brand-500';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">일일 사용량 관리</h1>

      {/* 전역 설정 */}
      <Card padding="xl" radius="lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">전역 설정</h2>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                config?.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {config?.isEnabled ? '활성화됨' : '비활성화됨'}
            </span>
            <button
              onClick={handleToggleEnabled}
              disabled={isSaving}
              className={`px-3 py-1 text-sm rounded ${
                config?.isEnabled
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {config?.isEnabled ? '비활성화' : '활성화'}
            </button>
          </div>
        </div>

        {editingConfig ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  일일 사진 제한
                </label>
                <input
                  type="number"
                  value={editingConfig.photoLimit}
                  onChange={(e) =>
                    setEditingConfig({ ...editingConfig, photoLimit: parseInt(e.target.value) || 0 })
                  }
                  className="w-full px-3 py-2 border rounded-control focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  일일 점검 제한
                </label>
                <input
                  type="number"
                  value={editingConfig.inspectionLimit}
                  onChange={(e) =>
                    setEditingConfig({
                      ...editingConfig,
                      inspectionLimit: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border rounded-control focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="primary" onClick={handleSaveConfig} disabled={isSaving}>
                {isSaving ? '저장 중...' : '저장'}
              </Button>
              <button
                onClick={() => setEditingConfig(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-control hover:bg-gray-200"
              >
                취소
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex gap-8">
              <div>
                <span className="text-sm text-gray-500">사진</span>
                <p className="text-lg font-semibold">{config?.photoLimit}장/일</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">점검</span>
                <p className="text-lg font-semibold">{config?.inspectionLimit}회/일</p>
              </div>
            </div>
            <button
              onClick={() =>
                setEditingConfig({
                  photoLimit: config?.photoLimit ?? 100,
                  inspectionLimit: config?.inspectionLimit ?? 5,
                })
              }
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-control hover:bg-gray-200"
            >
              수정
            </button>
          </div>
        )}
      </Card>

      {/* 사용자 목록 */}
      <Card padding="none" radius="lg">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">사용자별 사용량</h2>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">날짜:</label>
            <DateInput label="조회 날짜" value={selectedDate} onChange={setSelectedDate} size="sm" />
          </div>
        </div>

        {usages.length === 0 ? (
          <div className="p-8 text-center text-gray-500">해당 날짜에 사용량 기록이 없습니다.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사용자
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사진
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    점검
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {usages.map((usage) => (
                  <tr key={usage.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-gray-900">{usage.userName}</div>
                        {usage.userIdentifier && (
                          <div className="text-sm text-gray-500">{usage.userIdentifier}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <InlineEditableNumber
                        value={usage.photoCount}
                        max={config?.photoLimit ?? 100}
                        colorClass={getUsageColor(usage.photoCount, config?.photoLimit ?? 100)}
                        onSave={(newValue) => handleUpdateUsage(usage.userId, 'photoCount', newValue)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <InlineEditableNumber
                        value={usage.inspectionCount}
                        max={config?.inspectionLimit ?? 5}
                        colorClass={getUsageColor(usage.inspectionCount, config?.inspectionLimit ?? 5)}
                        onSave={(newValue) => handleUpdateUsage(usage.userId, 'inspectionCount', newValue)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <TextButton
                        className="text-sm"
                        onClick={() => handleResetUsage(usage.userId, usage.userName)}
                      >
                        초기화
                      </TextButton>
                      {usage.lastResetAt && (
                        <span className="ml-2 text-xs text-gray-400">
                          (마지막 초기화: {new Date(usage.lastResetAt).toLocaleString('ko-KR')})
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > 0 && (
          <div className="px-6 py-3 border-t text-sm text-gray-500">총 {total}명</div>
        )}
      </Card>
      {confirmDialog}
    </div>
  );
}
